import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../utils/api';
import { useI18n } from '../../../hooks/useI18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PlusIcon, PlayIcon, PencilIcon, TrashIcon, SearchIcon } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";

interface MetricDefinition {
  id: string;
  metric_code: string;
  display_name: string;
  description?: string;
  category?: string;
  metric_type: string;
  aggregation?: string;
  source_table?: string;
  source_field?: string;
  value_type: string;
  supported_dimensions?: string[];
  is_system: boolean;
  created_at: string;
}

export default function MetricsListPage() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/api/metrics/definitions');

      if (!response.ok) {
        throw new Error('Failed to fetch metrics');
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (metricId: string) => {
    if (!confirm(t('metrics.confirmDelete', 'common'))) {
      return;
    }

    try {
      const response = await apiFetch(`/api/metrics/definitions/${metricId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(t('metrics.deleteError', 'common'));
      }

      await fetchMetrics();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('metrics.deleteError', 'common'));
    }
  };

  const filteredMetrics = metrics.filter(metric => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      metric.display_name.toLowerCase().includes(search) ||
      metric.metric_code.toLowerCase().includes(search) ||
      (metric.description?.toLowerCase().includes(search)) ||
      (metric.category?.toLowerCase().includes(search))
    );
  });

  const groupedMetrics = filteredMetrics.reduce((acc, metric) => {
    const category = metric.category || t('metrics.categories.other', 'common');
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(metric);
    return acc;
  }, {} as Record<string, MetricDefinition[]>);

  const getMetricTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      simple: 'bg-blue-100 text-blue-800',
      ratio: 'bg-purple-100 text-purple-800',
      derived: 'bg-green-100 text-green-800',
      custom_sql: 'bg-orange-100 text-orange-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getValueTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      currency: 'bg-emerald-100 text-emerald-800',
      percentage: 'bg-amber-100 text-amber-800',
      count: 'bg-cyan-100 text-cyan-800',
      decimal: 'bg-slate-100 text-slate-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{t('metrics.title', 'common')}</h1>
            <p className="text-gray-600 mt-1">{t('metrics.subtitle', 'common')}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate({ to: '/metrics/$id', params: { id: 'new' } })}
              className="flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              {t('metrics.newMetric', 'common')}
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder={t('metrics.searchPlaceholder', 'common')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-gray-500">{t('metrics.loadingMetrics', 'common')}</p>
            </CardContent>
          </Card>
        )}

        {/* Metrics by Category */}
        {!loading && Object.keys(groupedMetrics).length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">{t('metrics.noMetricsFound', 'common')}</p>
                <Button onClick={() => navigate({ to: '/metrics/$id', params: { id: 'new' } })}>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('metrics.createFirstMetric', 'common')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && Object.entries(groupedMetrics).map(([category, categoryMetrics]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="capitalize">{category}</CardTitle>
              <CardDescription>{categoryMetrics.length} {t('metrics.metricsCount', 'common')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('metrics.name', 'common')}</TableHead>
                    <TableHead>{t('metrics.code', 'common')}</TableHead>
                    <TableHead>{t('metrics.metricType', 'common')}</TableHead>
                    <TableHead>{t('metrics.source', 'common')}</TableHead>
                    <TableHead>{t('metrics.valueType', 'common')}</TableHead>
                    <TableHead>{t('metrics.dimensions', 'common')}</TableHead>
                    <TableHead className="text-right">{t('actions.actions', 'common')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryMetrics.map((metric) => (
                    <TableRow key={metric.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metric.display_name}</div>
                          {metric.description && (
                            <div className="text-sm text-gray-500">{metric.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {metric.metric_code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge className={getMetricTypeBadge(metric.metric_type)}>
                          {metric.metric_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {metric.source_table && (
                          <code className="text-xs">
                            {metric.source_table}.{metric.source_field}
                          </code>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getValueTypeBadge(metric.value_type)}>
                          {metric.value_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-gray-500">
                          {metric.supported_dimensions?.length || 0} {t('metrics.dimensionsCount', 'common')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate({ to: `/metrics/execute/${metric.id}` })}
                          >
                            <PlayIcon className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate({ to: `/metrics/${metric.id}` })}
                          >
                            <PencilIcon className="h-3 w-3" />
                          </Button>
                          {!metric.is_system && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(metric.id)}
                            >
                              <TrashIcon className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>
    </Layout>
  );
}

export const Route = createFileRoute('/metrics/')({
  component: MetricsListPage,
})
